import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * PluginLibraryBrowser — Plugin-driven library browser component.
 *
 * Renders a multi-section library layout with:
 * - Device memory panel slot (when deviceMemory config present)
 * - Library sections driven by plugin categories
 * - Preview panel slot
 *
 * The layout adapts based on plugin configuration:
 * - Three-column when deviceMemory present: [Device | Library | Preview]
 * - Two-column when no device memory: [Library | Preview]
 *
 * Device-agnostic — all device-specific behavior comes from the plugin.
 */
import { useCallback, useRef, useState } from 'react';
import { LoadingBar } from './LoadingBar';
import { CreateFolderDialog } from './CreateFolderDialog';
import { MoveDialog } from './MoveDialog';
import { TreeSection } from './TreeSection';
/** Custom MIME type for dragging library items. */
export const LIBRARY_ITEM_MIME = 'application/x-library-item';
import { ContextMenu } from './ContextMenu';
// =========================================================================
// PluginLibraryBrowser Component
// =========================================================================
/** Flatten a tree into all nodes (for resolving selected IDs to TreeNode objects). */
function flattenAllNodes(nodes) {
    const result = [];
    for (const node of nodes) {
        result.push(node);
        if (node.children)
            result.push(...flattenAllNodes(node.children));
    }
    return result;
}
/** Flatten a tree of nodes into a list of IDs in tree order (for shift-click range). */
function flattenNodeIds(nodes) {
    const result = [];
    for (const node of nodes) {
        if (node.type !== 'directory')
            result.push(node.id);
        if (node.children)
            result.push(...flattenNodeIds(node.children));
    }
    return result;
}
/** Flatten a tree of nodes into a list of directories for MoveDialog. */
function flattenDirectories(nodes, path = [], depth = 0) {
    const result = [];
    for (const node of nodes) {
        if (node.type !== 'directory')
            continue;
        result.push({ id: node.id, name: node.name, path, depth });
        if (node.children) {
            result.push(...flattenDirectories(node.children, [...path, node.name], depth + 1));
        }
    }
    return result;
}
/** Returns true if targetPath is NOT inside the source node's subtree. */
function isValidMoveTarget(sourceNode, sourcePath, targetPath) {
    const sourceFullPath = [...sourcePath, sourceNode.name].join('/');
    const targetFullPathStr = targetPath.join('/');
    // Can't move to same parent
    if (sourcePath.join('/') === targetFullPathStr)
        return false;
    // Can't move into self or descendants
    if (targetFullPathStr.startsWith(sourceFullPath + '/') || targetFullPathStr === sourceFullPath)
        return false;
    return true;
}
// =========================================================================
// Multi-select preview panel
// =========================================================================
function MultiSelectPreview({ count, categoryId, selectedIds, categoryData, plugin, onContextMenuAction, onBatchMove, onBatchDelete, clearSelection, openMoveDialog, }) {
    // Determine item type label from the first selected node
    const data = categoryData[categoryId] ?? [];
    const allNodes = flattenAllNodes(data);
    const selected = allNodes.filter((n) => selectedIds.has(n.id));
    const firstNode = selected[0];
    const category = plugin.categories.find((c) => c.categoryId === categoryId);
    const itemTypePlugin = firstNode && category ? category.itemTypes[firstNode.type] : undefined;
    const typeName = itemTypePlugin?.displayName ?? 'Item';
    const typeLabel = count === 1 ? typeName : `${typeName}s`;
    // Get batchable actions from the item type
    const batchActions = [];
    if (itemTypePlugin?.getContextMenuActions && firstNode) {
        const actions = itemTypePlugin.getContextMenuActions(firstNode.meta, firstNode);
        const fileOps = new Set(['rename', 'delete', 'move']);
        for (const action of actions) {
            if ('separator' in action)
                continue;
            const a = action;
            if (!a.batchable || fileOps.has(a.id))
                continue;
            batchActions.push(a);
        }
    }
    return (_jsxs("div", { className: "p-4", children: [_jsxs("h3", { className: "text-lg font-semibold text-gray-100 mb-3", children: [count, " ", typeLabel, " Selected"] }), _jsxs("div", { className: "flex gap-2 flex-wrap", children: [batchActions.map((action) => (_jsx("button", { className: "px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors", onClick: () => {
                            for (const node of selected) {
                                onContextMenuAction?.(categoryId, action.id, node);
                            }
                            clearSelection();
                        }, children: action.label }, action.id))), onBatchMove && (_jsx("button", { className: "px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors", onClick: () => {
                            if (firstNode)
                                openMoveDialog(categoryId, firstNode);
                        }, children: "Move..." })), onBatchDelete && (_jsx("button", { className: "px-3 py-1.5 text-sm bg-red-700 hover:bg-red-600 text-white rounded transition-colors", onClick: () => {
                            void onBatchDelete(categoryId, selected);
                            clearSelection();
                        }, children: "Delete" }))] })] }));
}
export function PluginLibraryBrowser({ plugin, libraryHandle, categoryData, expandedPaths, selection, onSelectionChange, onToggleExpand, onRefresh, onCreateFolder, onDelete, onRename, onContextMenuAction, onMove, onExternalDrop, onFileDrop, onBatchDelete, onBatchMove, deviceMemoryState, onDeviceMemoryAction, previewState, loading, error, operationProgress, connectionSlot, headerSections, devicePanelLeft, }) {
    const hasDeviceMemory = !!plugin.deviceMemory;
    const fileInputRef = useRef(null);
    const fileInputCategoryRef = useRef('');
    // Move dialog state
    const [moveDialog, setMoveDialog] = useState(null);
    // Create folder drawer state
    const [createFolderDrawer, setCreateFolderDrawer] = useState(null);
    // Multi-select state
    const [multiSelectedIds, setMultiSelectedIds] = useState(new Set());
    const [multiSelectCategoryId, setMultiSelectCategoryId] = useState(null);
    const lastSelectedIdRef = useRef(null);
    const handleMultiSelect = useCallback((categoryId, node, modifiers) => {
        // Multi-select only works within a single category
        if (multiSelectCategoryId && multiSelectCategoryId !== categoryId) {
            // Switching categories — reset
            setMultiSelectedIds(new Set([node.id]));
            setMultiSelectCategoryId(categoryId);
            lastSelectedIdRef.current = node.id;
            return;
        }
        if (modifiers.shiftKey && lastSelectedIdRef.current) {
            // Range select: find all nodes between last selected and current
            const nodes = categoryData[categoryId] ?? [];
            const flatIds = flattenNodeIds(nodes);
            const lastIdx = flatIds.indexOf(lastSelectedIdRef.current);
            const currIdx = flatIds.indexOf(node.id);
            if (lastIdx >= 0 && currIdx >= 0) {
                const start = Math.min(lastIdx, currIdx);
                const end = Math.max(lastIdx, currIdx);
                const rangeIds = flatIds.slice(start, end + 1);
                setMultiSelectedIds(new Set([...multiSelectedIds, ...rangeIds]));
            }
        }
        else {
            // Toggle select (Ctrl/Cmd) — first Ctrl-click after a plain
            // click needs to seed the prior single-selection (the anchor)
            // into the set, otherwise TreeView's `isSelected` check
            // (`selectedIds.has(id)`) ignores the anchor's `selectedId`
            // entirely and the anchor row reads as unselected. The user
            // sees their original click "vanish" the moment they
            // Ctrl-click a sibling.
            const next = new Set(multiSelectedIds);
            if (next.size === 0 && lastSelectedIdRef.current && lastSelectedIdRef.current !== node.id) {
                next.add(lastSelectedIdRef.current);
            }
            if (next.has(node.id)) {
                next.delete(node.id);
            }
            else {
                next.add(node.id);
            }
            setMultiSelectedIds(next);
            lastSelectedIdRef.current = node.id;
        }
        setMultiSelectCategoryId(categoryId);
    }, [categoryData, multiSelectedIds, multiSelectCategoryId]);
    // Plain click clears multi-selection
    const handleSelect = useCallback((categoryId, node) => {
        setMultiSelectedIds(new Set());
        setMultiSelectCategoryId(null);
        lastSelectedIdRef.current = node.id;
        onSelectionChange({
            categoryId,
            node,
            meta: node.meta ?? {},
        });
    }, [onSelectionChange]);
    const handleFileInputChange = useCallback((e) => {
        const files = e.target.files;
        if (!files || files.length === 0 || !onFileDrop)
            return;
        const categoryId = fileInputCategoryRef.current;
        void onFileDrop(categoryId, Array.from(files), []);
        // Reset so the same file can be selected again
        e.target.value = '';
    }, [onFileDrop]);
    // Create category callbacks factory
    const createCategoryCallbacks = useCallback((categoryId) => ({
        refresh: onRefresh,
        createFolder: () => setCreateFolderDrawer({ open: true, categoryId, parentPath: [] }),
        importFiles: () => {
            fileInputCategoryRef.current = categoryId;
            fileInputRef.current?.click();
        },
    }), [onRefresh, onCreateFolder]);
    // Handle rename with category context
    const handleRename = useCallback((categoryId) => async (node, newName) => {
        await onRename(categoryId, node, newName);
    }, [onRename]);
    // Handle delete with category context
    const handleDelete = useCallback((categoryId) => (node) => {
        onDelete(categoryId, node);
    }, [onDelete]);
    // Context menu state
    const [contextMenu, setContextMenu] = useState(null);
    // Handle context menu with category context — opens the plugin-driven menu
    const handleContextMenu = useCallback((categoryId) => (e, node) => {
        e.preventDefault();
        const category = plugin.categories.find((c) => c.categoryId === categoryId);
        if (!category)
            return;
        const itemTypePlugin = category.itemTypes[node.type];
        if (!itemTypePlugin?.getContextMenuActions)
            return;
        const actions = itemTypePlugin.getContextMenuActions(node.meta, node);
        if (actions.length === 0)
            return;
        setContextMenu({ x: e.clientX, y: e.clientY, categoryId, node });
    }, [plugin.categories]);
    // Is the context menu target part of a multi-selection?
    const isBatchContext = contextMenu
        && multiSelectedIds.size > 1
        && multiSelectedIds.has(contextMenu.node.id)
        && multiSelectCategoryId === contextMenu.categoryId;
    // Build ContextMenuAction[] from plugin actions for the currently open menu
    const contextMenuActions = (() => {
        if (!contextMenu)
            return [];
        // Batch context menu — transfer actions + move + delete for multiple items
        if (isBatchContext) {
            const count = multiSelectedIds.size;
            const { categoryId, node } = contextMenu;
            const actions = [];
            // Include transfer actions from the item type plugin (Send to Device, etc.)
            const category = plugin.categories.find((c) => c.categoryId === categoryId);
            if (category) {
                const itemTypePlugin = category.itemTypes[node.type];
                if (itemTypePlugin?.getContextMenuActions) {
                    const pluginActions = itemTypePlugin.getContextMenuActions(node.meta, node);
                    // Only include batchable actions (not file ops — those are handled below)
                    const fileOps = new Set(['rename', 'delete', 'move']);
                    for (const action of pluginActions) {
                        if ('separator' in action)
                            continue;
                        const a = action;
                        if (fileOps.has(a.id))
                            continue;
                        if (!a.batchable)
                            continue;
                        actions.push({
                            label: a.label,
                            icon: a.icon,
                            onClick: () => {
                                // Fire the action for each selected node
                                const data = categoryData[categoryId] ?? [];
                                const allNodes = flattenAllNodes(data);
                                const selected = allNodes.filter((n) => multiSelectedIds.has(n.id));
                                for (const n of selected) {
                                    onContextMenuAction?.(categoryId, a.id, n);
                                }
                                setMultiSelectedIds(new Set());
                                setMultiSelectCategoryId(null);
                            },
                        });
                    }
                }
            }
            // Move
            if (onBatchMove) {
                if (actions.length > 0) {
                    actions.push({ label: '', onClick: () => { }, separator: true });
                }
                actions.push({
                    label: `Move ${count} items...`,
                    onClick: () => {
                        setMoveDialog({ open: true, categoryId, node });
                    },
                });
            }
            // Delete
            if (onBatchDelete) {
                if (actions.length > 0) {
                    actions.push({ label: '', onClick: () => { }, separator: true });
                }
                actions.push({
                    label: `Delete ${count} items`,
                    danger: true,
                    onClick: () => {
                        // Batch delete is triggered from a context menu — the user already
                        // chose "Delete N items" deliberately. No second confirmation needed.
                        const data = categoryData[categoryId] ?? [];
                        const allNodes = flattenAllNodes(data);
                        const selected = allNodes.filter((n) => multiSelectedIds.has(n.id));
                        void onBatchDelete(categoryId, selected);
                        setMultiSelectedIds(new Set());
                        setMultiSelectCategoryId(null);
                    },
                });
            }
            return actions;
        }
        // Single-item context menu
        const category = plugin.categories.find((c) => c.categoryId === contextMenu.categoryId);
        if (!category)
            return [];
        const itemTypePlugin = category.itemTypes[contextMenu.node.type];
        if (!itemTypePlugin?.getContextMenuActions)
            return [];
        const pluginActions = itemTypePlugin.getContextMenuActions(contextMenu.node.meta, contextMenu.node);
        return pluginActions.map((action) => {
            if ('separator' in action) {
                return { label: '---', separator: true, onClick: () => { } };
            }
            const menuAction = action;
            return {
                label: menuAction.label,
                icon: menuAction.icon,
                disabled: menuAction.disabled,
                danger: menuAction.danger,
                onClick: () => {
                    const { categoryId, node } = contextMenu;
                    if (menuAction.id === 'delete') {
                        onDelete(categoryId, node);
                    }
                    else if (menuAction.id === 'move') {
                        setMoveDialog({ open: true, categoryId, node });
                    }
                    else {
                        onContextMenuAction?.(categoryId, menuAction.id, node);
                    }
                },
            };
        });
    })();
    // Render icon for a node using the appropriate item type plugin
    const renderIcon = useCallback((categoryId) => (node, isExpanded) => {
        const category = plugin.categories.find((c) => c.categoryId === categoryId);
        if (!category)
            return null;
        // Directories use default folder icon (from TreeView)
        if (node.type === 'directory')
            return undefined;
        const itemTypePlugin = category.itemTypes[node.type];
        if (!itemTypePlugin)
            return null;
        const isSelected = selection?.node.id === node.id;
        return itemTypePlugin.renderIcon(node.meta ?? {}, isSelected);
    }, [plugin.categories, selection]);
    // Render trailing content for a node
    const renderTrailing = useCallback((categoryId) => (node) => {
        const category = plugin.categories.find((c) => c.categoryId === categoryId);
        if (!category || node.type === 'directory')
            return null;
        const itemTypePlugin = category.itemTypes[node.type];
        return itemTypePlugin?.renderTrailing?.(node.meta ?? {}) ?? null;
    }, [plugin.categories]);
    // Check if items in a category are draggable
    const isDraggable = useCallback((categoryId) => {
        const category = plugin.categories.find((c) => c.categoryId === categoryId);
        if (!category)
            return false;
        // If any item type is draggable, enable dragging for the category
        return Object.values(category.itemTypes).some((itemType) => itemType.isDraggable({}));
    }, [plugin.categories]);
    // Check if items in a category support rename
    const supportsRename = useCallback((categoryId) => {
        const category = plugin.categories.find((c) => c.categoryId === categoryId);
        if (!category || category.isReadOnly)
            return false;
        // If any item type supports rename, enable it for the category
        return Object.values(category.itemTypes).some((itemType) => itemType.supportsRename);
    }, [plugin.categories]);
    // External drop handling (e.g., disk browser items)
    const [dropTargetCategory, setDropTargetCategory] = useState(null);
    const [dropIsMove, setDropIsMove] = useState(false);
    const activeDragRef = useRef(null);
    const handleSectionDragOver = useCallback((categoryId) => (e) => {
        // Library-item drag = move to category root (skip if already at root)
        if (e.dataTransfer.types.includes(LIBRARY_ITEM_MIME)) {
            const drag = activeDragRef.current;
            if (drag && drag.sourcePath.length === 0)
                return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropTargetCategory(categoryId);
            setDropIsMove(true);
            return;
        }
        // External drops: only highlight if the category declares it
        // accepts at least one of the MIME types in this drag. Without
        // this check both sections highlight for any device-item drag
        // even though only the matching section actually accepts the
        // drop — operator sees two glowing targets and only one works.
        // Categories that don't declare `acceptedDropMimeTypes` fall
        // back to the legacy "accept anything" behavior so non-Roland
        // editors (akai-s3k, etc.) aren't broken by this guard.
        if (!onExternalDrop)
            return;
        const category = plugin.categories.find((c) => c.categoryId === categoryId);
        const accepted = category?.acceptedDropMimeTypes;
        if (accepted && accepted.length > 0) {
            const types = e.dataTransfer.types;
            const matched = accepted.some((mime) => types.includes(mime));
            if (!matched)
                return;
        }
        else if (e.dataTransfer.types.length === 0) {
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDropTargetCategory(categoryId);
        setDropIsMove(false);
    }, [onExternalDrop, plugin.categories]);
    const handleSectionDragLeave = useCallback(() => (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDropTargetCategory(null);
            setDropIsMove(false);
        }
    }, []);
    const handleSectionDrop = useCallback((categoryId) => (e) => {
        e.preventDefault();
        setDropTargetCategory(null);
        setDropIsMove(false);
        activeDragRef.current = null;
        // Library-item drop = move to category root
        const raw = e.dataTransfer.getData(LIBRARY_ITEM_MIME);
        if (raw) {
            const payload = JSON.parse(raw);
            if (payload.categoryId === categoryId) {
                // Batch move if dragged item is part of a multi-selection
                if (multiSelectedIds.size > 1 && multiSelectedIds.has(payload.nodeId) && onBatchMove) {
                    const data = categoryData[categoryId] ?? [];
                    const allNodes = flattenAllNodes(data);
                    const selected = allNodes.filter((n) => multiSelectedIds.has(n.id));
                    void onBatchMove(categoryId, selected, []);
                    setMultiSelectedIds(new Set());
                    setMultiSelectCategoryId(null);
                }
                else {
                    void onMove(categoryId, {
                        id: payload.nodeId,
                        name: payload.nodeName,
                        type: payload.nodeType,
                        children: [],
                        // Preserve the original node's meta (carries `fileName` /
                        // `directoryName` for `getNodeName`) so move handlers
                        // resolve the on-disk slug rather than the display name.
                        // Without the spread, any item whose YAML `name` differs
                        // from its directory slug fails with "file or directory
                        // could not be found." Re-discovered by D-LIB-22.
                        meta: { ...payload.meta, path: payload.sourcePath },
                    }, []);
                }
            }
            return;
        }
        // External drop
        onExternalDrop?.(categoryId, e.dataTransfer, []);
    }, [onExternalDrop, onMove, onBatchMove, multiSelectedIds, multiSelectCategoryId, categoryData]);
    // Layout classes
    const layoutClass = hasDeviceMemory
        ? 'ac-plugin-library-browser ac-plugin-library-browser--three-column'
        : 'ac-plugin-library-browser ac-plugin-library-browser--two-column';
    return (_jsxs("div", { className: layoutClass, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: "audio/wav,audio/x-wav,.wav", multiple: true, style: { display: 'none' }, onChange: handleFileInputChange }), devicePanelLeft && (_jsx("div", { className: "ac-plugin-library-browser-device-left", children: devicePanelLeft })), hasDeviceMemory && plugin.deviceMemory && (_jsx("div", { className: "ac-plugin-library-browser-device", children: plugin.deviceMemory.renderMemoryPanel({
                    selectedSlot: null, // Selection is typically managed via customState
                    onSelectSlot: (groupId, index) => {
                        onDeviceMemoryAction?.({
                            type: 'SELECT_SLOT',
                            payload: { groupId, index },
                        });
                    },
                    onDragStart: (groupId, index, dragData) => {
                        onDeviceMemoryAction?.({
                            type: 'DRAG_START',
                            payload: { groupId, index, dragData },
                        });
                    },
                    onDrop: (groupId, index, dragData) => {
                        onDeviceMemoryAction?.({
                            type: 'DROP',
                            payload: { groupId, index, dragData },
                        });
                    },
                    // Pass opaque state from consumer to plugin
                    customState: deviceMemoryState,
                }) })), _jsxs("div", { className: "ac-plugin-library-browser-library", children: [_jsxs("div", { className: "ac-panel-header", children: [_jsx("span", { className: "ac-panel-header-title", children: "Library" }), _jsxs("div", { className: "ac-panel-header-actions", children: [connectionSlot, _jsx("button", { type: "button", className: "ac-panel-refresh-btn", onClick: onRefresh, title: "Refresh library", children: "\u21BB" })] })] }), _jsx(LoadingBar, { active: loading ?? false }), loading && (_jsx("div", { className: "ac-plugin-library-browser-loading", children: _jsx("div", { className: "ac-plugin-library-browser-skeleton", children: plugin.categories.slice(0, 3).map((cat) => (_jsxs("div", { className: "ac-plugin-library-browser-skeleton-section", children: [_jsx("div", { className: "ac-skeleton ac-skeleton-text--short", style: { marginBottom: '0.5rem' } }), _jsx("div", { className: "ac-skeleton ac-skeleton-text--medium", style: { marginBottom: '0.25rem' } }), _jsx("div", { className: "ac-skeleton ac-skeleton-text", style: { marginBottom: '0.25rem' } }), _jsx("div", { className: "ac-skeleton ac-skeleton-text--short" })] }, cat.categoryId))) }) })), error && (_jsxs("div", { style: {
                            margin: 'var(--ac-space-2) var(--ac-space-3)',
                            padding: 'var(--ac-space-2) var(--ac-space-3)',
                            background: 'color-mix(in srgb, var(--ac-color-danger) 12%, transparent)',
                            border: '1px solid color-mix(in srgb, var(--ac-color-danger) 30%, transparent)',
                            borderRadius: 'var(--ac-radius-sm)',
                            fontSize: 'var(--ac-text-sm)',
                            color: 'var(--ac-color-danger)',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--ac-space-2)',
                        }, children: [_jsx("span", { style: { flex: 1 }, children: error }), _jsx("button", { style: { background: 'none', border: 'none', color: 'var(--ac-color-danger)', cursor: 'pointer', padding: 0, fontSize: '1rem' }, onClick: onRefresh, title: "Dismiss", children: "\u00D7" })] })), !loading && !libraryHandle && (_jsxs("div", { className: "ac-plugin-library-browser-empty-state", children: [_jsx("svg", { className: "ac-plugin-library-browser-empty-icon", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, children: _jsx("path", { d: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" }) }), _jsx("p", { className: "ac-plugin-library-browser-empty-text", children: "Connect to a library folder to get started" })] })), !loading && libraryHandle && (() => {
                        // Scope-grouping: render two visually-distinct bands so the
                        // operator immediately sees which categories travel with
                        // the connected device (`library/<device>/<category>/`) vs
                        // which survive an editor switch (`library/common/<category>/`).
                        // Categories opt in via `scope: 'device' | 'common'`; default
                        // is 'device' so existing factories without the field stay
                        // under the device band.
                        const deviceCategories = plugin.categories.filter((c) => (c.scope ?? 'device') === 'device');
                        const commonCategories = plugin.categories.filter((c) => c.scope === 'common');
                        const renderCategorySection = (category) => (_jsx(TreeSection, { "data-testid": `library-${category.categoryId}-section`, title: category.title, nodes: categoryData[category.categoryId] ?? [], category: category.categoryId, expandedIds: expandedPaths[category.categoryId] ?? new Set(), selectedId: selection?.categoryId === category.categoryId
                                ? selection.node.id
                                : undefined, onToggleExpand: (nodeId) => onToggleExpand(category.categoryId, nodeId), selectedIds: multiSelectCategoryId === category.categoryId ? multiSelectedIds : undefined, selection: {
                                onSelect: (node) => handleSelect(category.categoryId, node),
                                onMultiSelect: (node, mods) => handleMultiSelect(category.categoryId, node, mods),
                            }, edit: category.isReadOnly ? undefined : {
                                onDelete: handleDelete(category.categoryId),
                                onRename: supportsRename(category.categoryId)
                                    ? handleRename(category.categoryId)
                                    : undefined,
                                onCreateFolder: (parentNode) => setCreateFolderDrawer({
                                    open: true,
                                    categoryId: category.categoryId,
                                    parentPath: [...(parentNode.meta?.path ?? []), parentNode.name],
                                }),
                                enableInlineRename: supportsRename(category.categoryId),
                            }, contextMenu: {
                                onContextMenu: handleContextMenu(category.categoryId),
                            }, drag: {
                                draggable: isDraggable(category.categoryId),
                                onDragStart: (node, e) => {
                                    const nodePath = node.meta?.path ?? [];
                                    const payload = {
                                        categoryId: category.categoryId,
                                        nodeId: node.id,
                                        nodeName: node.name,
                                        nodeType: node.type,
                                        sourcePath: nodePath,
                                        meta: node.meta ?? {},
                                    };
                                    activeDragRef.current = payload;
                                    e.dataTransfer.setData(LIBRARY_ITEM_MIME, JSON.stringify(payload));
                                    e.dataTransfer.setData(`${LIBRARY_ITEM_MIME}/${node.type}`, '');
                                    e.dataTransfer.effectAllowed = 'copyMove';
                                },
                                onTreeDragOver: (targetNode, e) => {
                                    // Accept library-item drags for same-category move
                                    if (e.dataTransfer.types.includes(LIBRARY_ITEM_MIME)) {
                                        const drag = activeDragRef.current;
                                        if (drag) {
                                            // Don't accept if dropping onto the item's current parent
                                            const targetPath = [...(targetNode.meta?.path ?? []), targetNode.name];
                                            if (drag.sourcePath.join('/') === targetPath.join('/'))
                                                return false;
                                            // Don't accept if dropping onto itself or a descendant
                                            const sourceFullPath = [...drag.sourcePath, drag.nodeName].join('/');
                                            if (targetPath.join('/').startsWith(sourceFullPath))
                                                return false;
                                        }
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                        return true;
                                    }
                                    // External drops (disk items, device-memory items):
                                    // only accept the hover if the category declares it
                                    // can take this MIME. Without the check, hovering a
                                    // tone drag over a folder INSIDE the patches section
                                    // still glows the folder even though the actual drop
                                    // gets rejected by `handleExternalDrop`'s category
                                    // gate — operator sees a valid-looking drop target
                                    // that silently does nothing. Same shape as the
                                    // section-level gate in `handleSectionDragOver`.
                                    if (!onExternalDrop)
                                        return false;
                                    const accepted = category.acceptedDropMimeTypes;
                                    if (accepted && accepted.length > 0) {
                                        const matched = accepted.some((mime) => e.dataTransfer.types.includes(mime));
                                        if (!matched)
                                            return false;
                                    }
                                    else if (e.dataTransfer.types.length === 0) {
                                        return false;
                                    }
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'copy';
                                    return true;
                                },
                                onTreeDrop: (node, e) => {
                                    setDropTargetCategory(null);
                                    setDropIsMove(false);
                                    activeDragRef.current = null;
                                    const nodePath = node.meta?.path ?? [];
                                    const targetPath = [...nodePath, node.name];
                                    // Library item move within same category
                                    const raw = e.dataTransfer.getData(LIBRARY_ITEM_MIME);
                                    if (raw) {
                                        const payload = JSON.parse(raw);
                                        if (payload.categoryId === category.categoryId) {
                                            // Batch move if the dragged item is part of a multi-selection
                                            if (multiSelectedIds.size > 1 && multiSelectedIds.has(payload.nodeId) && onBatchMove) {
                                                const data = categoryData[category.categoryId] ?? [];
                                                const allNodes = flattenAllNodes(data);
                                                const selected = allNodes.filter((n) => multiSelectedIds.has(n.id));
                                                void onBatchMove(category.categoryId, selected, targetPath);
                                                setMultiSelectedIds(new Set());
                                                setMultiSelectCategoryId(null);
                                            }
                                            else {
                                                void onMove(category.categoryId, {
                                                    id: payload.nodeId,
                                                    name: payload.nodeName,
                                                    type: payload.nodeType,
                                                    children: [],
                                                    // See the root-target equivalent above for
                                                    // why payload.meta must be spread here.
                                                    meta: { ...payload.meta, path: payload.sourcePath },
                                                }, targetPath);
                                            }
                                        }
                                        return;
                                    }
                                    // External drop
                                    if (onExternalDrop) {
                                        onExternalDrop(category.categoryId, e.dataTransfer, targetPath);
                                    }
                                },
                            }, render: {
                                renderIcon: renderIcon(category.categoryId),
                                renderTrailing: renderTrailing(category.categoryId),
                            }, emptyMessage: category.emptyMessage, dropMessage: dropIsMove ? 'Move to top level' : (category.dropMessage ?? 'Drop to add'), isDragOver: dropTargetCategory === category.categoryId, onDragOver: handleSectionDragOver(category.categoryId), onDragLeave: handleSectionDragLeave(), onDrop: handleSectionDrop(category.categoryId), headerActions: category.renderHeaderActions?.(createCategoryCallbacks(category.categoryId)) }, category.categoryId));
                        return (_jsxs("div", { className: "ac-plugin-library-browser-sections", children: [(deviceCategories.length > 0 || headerSections) && (_jsxs("div", { className: "ac-plib-group-head", "data-scope": "device", children: [_jsxs("span", { className: "ac-plib-group-eyebrow", children: ["Device ", _jsx("span", { className: "ac-plib-group-eyebrow-sep", children: "\u00B7" }), " ", plugin.deviceName] }), _jsx("span", { className: "ac-plib-group-rule", "aria-hidden": "true" })] })), headerSections, deviceCategories.map(renderCategorySection), commonCategories.length > 0 && (_jsxs("div", { className: "ac-plib-group-head", "data-scope": "common", children: [_jsx("span", { className: "ac-plib-group-eyebrow", children: "Common Library" }), _jsx("span", { className: "ac-plib-group-rule", "aria-hidden": "true" })] })), commonCategories.map(renderCategorySection)] }));
                    })(), operationProgress && (_jsxs("div", { className: "ac-plugin-library-browser-progress", children: [_jsx("span", { children: operationProgress.stepLabel }), operationProgress.bytesTotalAllSteps > 0 && (_jsx("div", { className: "ac-progress-bar", children: _jsx("div", { className: "ac-progress-bar-fill", style: { width: `${Math.min(100, (operationProgress.bytesSentAllSteps / operationProgress.bytesTotalAllSteps) * 100)}%` } }) }))] }))] }), _jsx("div", { className: "ac-plugin-library-browser-preview", "data-testid": "library-preview-panel", children: multiSelectedIds.size > 1 && multiSelectCategoryId ? (_jsx(MultiSelectPreview, { count: multiSelectedIds.size, categoryId: multiSelectCategoryId, selectedIds: multiSelectedIds, categoryData: categoryData, plugin: plugin, onContextMenuAction: onContextMenuAction, onBatchMove: onBatchMove, onBatchDelete: onBatchDelete, clearSelection: () => {
                        setMultiSelectedIds(new Set());
                        setMultiSelectCategoryId(null);
                    }, openMoveDialog: (categoryId, node) => setMoveDialog({ open: true, categoryId, node }) })) : (plugin.previewPanel.renderPreview(selection, {
                    isLoading: loading ?? false,
                    error: error,
                    customState: previewState,
                })) }), contextMenu && contextMenuActions.length > 0 && (_jsx(ContextMenu, { x: contextMenu.x, y: contextMenu.y, actions: contextMenuActions, onClose: () => setContextMenu(null) })), moveDialog && (() => {
                const { categoryId } = moveDialog;
                const data = categoryData[categoryId] ?? [];
                const dirs = flattenDirectories(data);
                const isBatchMove = multiSelectedIds.size > 1 && multiSelectCategoryId === categoryId;
                const itemName = isBatchMove ? `${multiSelectedIds.size} items` : moveDialog.node.name;
                const sourcePath = moveDialog.node.meta?.path ?? [];
                return (_jsx(MoveDialog, { open: moveDialog.open, itemName: itemName, directories: dirs, isValidTarget: isBatchMove ? undefined : (targetPath) => isValidMoveTarget(moveDialog.node, sourcePath, targetPath), onMove: (targetPath) => {
                        if (isBatchMove && onBatchMove) {
                            const allNodes = flattenAllNodes(data);
                            const selected = allNodes.filter((n) => multiSelectedIds.has(n.id));
                            void onBatchMove(categoryId, selected, targetPath);
                            setMultiSelectedIds(new Set());
                            setMultiSelectCategoryId(null);
                        }
                        else {
                            void onMove(categoryId, moveDialog.node, targetPath);
                        }
                        setMoveDialog(null);
                    }, onCancel: () => setMoveDialog(null) }));
            })(), _jsx(CreateFolderDialog, { open: createFolderDrawer?.open ?? false, parentPath: createFolderDrawer?.parentPath ?? [], onConfirm: (name) => {
                    if (createFolderDrawer) {
                        void onCreateFolder(createFolderDrawer.categoryId, createFolderDrawer.parentPath, name);
                    }
                    setCreateFolderDrawer(null);
                }, onCancel: () => setCreateFolderDrawer(null) })] }));
}
//# sourceMappingURL=PluginLibraryBrowser.js.map